/** @jsxImportSource @emotion/react */
import React, { useMemo, useCallback } from 'react'
import { css } from '@emotion/react'
import { Popover } from 'antd'
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core'
import colors from '@sportninja/common/constants/appColors'
import CalendarGameCard, { CalendarGame } from './CalendarGameCard'
import { isDateBlackout, getGamesForDate } from './CalendarGrid'
import type { CalendarDay } from './CalendarGrid'
import type { TimeSlot } from '../hooks/useSchedulerTimeSlots'
import {
  getTimeRangesForFacilityAndDay,
  splitIntoHourSlots,
  computeConstraintBlockedDates,
  formatLocalDate,
} from '../utils/gameEditHelpers'
import type { SchedulerConstraint } from '../hooks/useSchedulerConstraints'

interface WeekGridProps {
  weekStart: Date // Sunday of the week to display
  games: CalendarGame[]
  blackoutDates: Date[]
  onGameClick?: (game: CalendarGame) => void
  popoverGameId?: string | null
  popoverContent?: React.ReactNode
  onPopoverOpenChange?: (gameId: string | null) => void
  onDragEnd?: (gameId: string, targetDate: Date) => void
  timeSlots?: TimeSlot[]
  competitionStartDate?: string
  competitionEndDate?: string
  constraints?: SchedulerConstraint[]
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_ABBREVS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

// ─── Draggable Game Card Wrapper ────────────────────────────────

interface DraggableWeekGameCardProps {
  game: CalendarGame
  onGameClick?: (game: CalendarGame) => void
  isPopoverOpen: boolean
  popoverContent?: React.ReactNode
  onPopoverOpenChange?: (gameId: string | null) => void
}

const DraggableWeekGameCard: React.FC<DraggableWeekGameCardProps> = ({
  game,
  onGameClick,
  isPopoverOpen,
  popoverContent,
  onPopoverOpenChange,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `week-game-${game.id}`,
    data: { game },
  })

  const handleClick = useCallback(
    (clickedGame: CalendarGame) => {
      onGameClick?.(clickedGame)
    },
    [onGameClick]
  )

  const cardContent = (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      css={css`
        opacity: ${isDragging ? 0.4 : 1};
        touch-action: none;
      `}
    >
      <CalendarGameCard game={game} onClick={handleClick} />
    </div>
  )

  if (popoverContent && onPopoverOpenChange) {
    return (
      <Popover
        content={popoverContent}
        trigger='click'
        open={isPopoverOpen}
        onOpenChange={(visible) => {
          if (!visible) {
            onPopoverOpenChange(null)
          }
        }}
        placement='rightTop'
        // eslint-disable-next-line react-native/no-inline-styles
        overlayStyle={{ zIndex: 1050 }}
        // eslint-disable-next-line react-native/no-inline-styles
        overlayInnerStyle={{
          background: colors.SECONDARY_300,
          border: `1px solid ${colors.SECONDARY_100}`,
          borderRadius: '8px',
          padding: 0,
        }}
        destroyTooltipOnHide
      >
        {cardContent}
      </Popover>
    )
  }

  return cardContent
}

// ─── Droppable Day Column ─────────────────────────────────────

interface DroppableWeekDayProps {
  day: CalendarDay
  onGameClick?: (game: CalendarGame) => void
  popoverGameId?: string | null
  popoverContent?: React.ReactNode
  onPopoverOpenChange?: (gameId: string | null) => void
  dndEnabled: boolean
  isDragging: boolean
  isFeasible: boolean
}

const DroppableWeekDay: React.FC<DroppableWeekDayProps> = ({
  day,
  onGameClick,
  popoverGameId,
  popoverContent,
  onPopoverOpenChange,
  dndEnabled,
  isDragging,
  isFeasible,
}) => {
  const dateISO = day.date.toISOString().slice(0, 10)

  const { setNodeRef, isOver } = useDroppable({
    id: `week-day-${dateISO}`,
    data: { date: day.date, isBlackout: day.isBlackout },
    disabled: !dndEnabled || day.isBlackout,
  })

  const dragHighlight =
    isDragging && !day.isBlackout
      ? isFeasible
        ? css`
            background: ${colors.SUCCESS_300}12;
            outline: 1px solid ${colors.SUCCESS_300}40;
            outline-offset: -1px;
          `
        : css`
            opacity: 0.35;
          `
      : undefined

  const hoverHighlight =
    isOver && !day.isBlackout
      ? css`
          outline: 2px dashed ${colors.PRIMARY_300};
          outline-offset: -2px;
        `
      : undefined

  return (
    <div
      ref={setNodeRef}
      css={[
        css`
          flex: 1;
          min-width: 0;
          min-height: 400px;
          padding: 8px;
          background: ${day.isBlackout
            ? colors.SECONDARY_600
            : day.isToday
            ? `${colors.PRIMARY_300}10`
            : colors.SECONDARY_500};
          border-right: 1px solid
            ${day.isToday ? colors.PRIMARY_300 : colors.SECONDARY_200};
          ${day.isToday
            ? `border-left: 1px solid ${colors.PRIMARY_300}; border-top: 1px solid ${colors.PRIMARY_300}; border-bottom: 1px solid ${colors.PRIMARY_300};`
            : ''}
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 4px;

          &:last-child {
            border-right: ${day.isToday
              ? `1px solid ${colors.PRIMARY_300}`
              : 'none'};
          }
        `,
        dragHighlight,
        hoverHighlight,
      ]}
    >
      {day.isBlackout && (
        <span
          css={css`
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-family: 'Inter', sans-serif;
            font-weight: 700;
            font-size: 12px;
            line-height: 12px;
            letter-spacing: 1px;
            color: ${colors.NEUTRAL_100};
            text-transform: uppercase;
            white-space: nowrap;
            z-index: 1;
          `}
        >
          BLACKOUT DAY
        </span>
      )}

      {!day.isBlackout && day.games.length > 0 && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            overflow-y: auto;
            flex: 1;

            &::-webkit-scrollbar {
              width: 4px;
            }

            &::-webkit-scrollbar-track {
              background: transparent;
            }

            &::-webkit-scrollbar-thumb {
              background: ${colors.NEUTRAL_100};
              border-radius: 2px;
            }
          `}
        >
          {day.games.map((game) => (
            <DraggableWeekGameCard
              key={game.id}
              game={game}
              onGameClick={onGameClick}
              isPopoverOpen={popoverGameId === game.id}
              popoverContent={
                popoverGameId === game.id ? popoverContent : undefined
              }
              onPopoverOpenChange={onPopoverOpenChange}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Week Grid Component ──────────────────────────────────────

const WeekGrid: React.FC<WeekGridProps> = ({
  weekStart,
  games,
  blackoutDates,
  onGameClick,
  popoverGameId,
  popoverContent,
  onPopoverOpenChange,
  onDragEnd: onDragEndProp,
  timeSlots = [],
  competitionStartDate,
  competitionEndDate,
  constraints = [],
}) => {
  const [activeGame, setActiveGame] = React.useState<CalendarGame | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  )

  const weekDays = useMemo((): CalendarDay[] => {
    const now = new Date()
    const todayYear = now.getFullYear()
    const todayMonth = now.getMonth()
    const todayDate = now.getDate()

    const days: CalendarDay[] = []
    for (let i = 0; i < 7; i++) {
      const date = new Date(
        weekStart.getFullYear(),
        weekStart.getMonth(),
        weekStart.getDate() + i
      )
      days.push({
        date,
        dayNumber: date.getDate(),
        isCurrentMonth: true,
        isToday:
          date.getFullYear() === todayYear &&
          date.getMonth() === todayMonth &&
          date.getDate() === todayDate,
        isBlackout: isDateBlackout(date, blackoutDates),
        games: getGamesForDate(date, games),
      })
    }
    return days
  }, [weekStart, games, blackoutDates])

  const preventScroll = useCallback((e: Event) => {
    e.preventDefault()
  }, [])

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const game = event.active.data.current?.game as CalendarGame | undefined
      setActiveGame(game ?? null)
      document.addEventListener('wheel', preventScroll, { passive: false })
      document.addEventListener('touchmove', preventScroll, { passive: false })
    },
    [preventScroll]
  )

  const cleanupDragScroll = useCallback(() => {
    document.removeEventListener('wheel', preventScroll)
    document.removeEventListener('touchmove', preventScroll)
  }, [preventScroll])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveGame(null)
      cleanupDragScroll()
      const { active, over } = event
      if (!over || !onDragEndProp) {
        return
      }

      const game = active.data.current?.game as CalendarGame | undefined
      const targetDate = over.data.current?.date as Date | undefined
      if (!game || !targetDate) {
        return
      }

      onDragEndProp(game.id, targetDate)
    },
    [onDragEndProp, cleanupDragScroll]
  )

  const handleDragCancel = useCallback(() => {
    setActiveGame(null)
    cleanupDragScroll()
  }, [cleanupDragScroll])

  const dndEnabled = !!onDragEndProp

  const compStart = useMemo(
    () =>
      competitionStartDate
        ? new Date(competitionStartDate + 'T00:00:00')
        : null,
    [competitionStartDate]
  )
  const compEnd = useMemo(
    () =>
      competitionEndDate ? new Date(competitionEndDate + 'T23:59:59') : null,
    [competitionEndDate]
  )

  const feasibleDaysOfWeek = useMemo(() => {
    if (!activeGame) {
      return new Set<number>()
    }
    const facilityId = activeGame.surfaceId
    if (!facilityId) {
      return new Set<number>()
    }
    const divisionId = activeGame.divisionId || undefined
    const days = new Set<number>()
    for (let dow = 0; dow < 7; dow++) {
      const ranges = getTimeRangesForFacilityAndDay(
        timeSlots,
        facilityId,
        dow,
        divisionId
      )
      if (splitIntoHourSlots(ranges).length > 0) {
        days.add(dow)
      }
    }
    return days
  }, [activeGame, timeSlots])

  const constraintBlockedDates = useMemo(() => {
    if (!activeGame || constraints.length === 0) {
      return new Set<string>()
    }
    return computeConstraintBlockedDates(activeGame, games, constraints)
  }, [activeGame, games, constraints])

  const gridContent = (
    <div
      css={css`
        display: flex;
        width: 100%;
      `}
    >
      {weekDays.map((day) => (
        <DroppableWeekDay
          key={day.date.toISOString()}
          day={day}
          onGameClick={onGameClick}
          popoverGameId={popoverGameId}
          popoverContent={popoverContent}
          onPopoverOpenChange={onPopoverOpenChange}
          dndEnabled={dndEnabled}
          isDragging={!!activeGame}
          isFeasible={
            !day.isBlackout &&
            feasibleDaysOfWeek.has(day.date.getDay()) &&
            (!compStart || day.date >= compStart) &&
            (!compEnd || day.date <= compEnd) &&
            !constraintBlockedDates.has(formatLocalDate(day.date))
          }
        />
      ))}
    </div>
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        width: 100%;
        background: ${colors.SECONDARY_500};
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid ${colors.SECONDARY_200};
      `}
    >
      {/* Header Row - Day names + dates */}
      <div
        css={css`
          display: flex;
          width: 100%;
          background: ${colors.SECONDARY_400};
          border-bottom: 1px solid ${colors.SECONDARY_200};
        `}
      >
        {weekDays.map((day) => (
          <div
            key={day.date.toISOString()}
            css={css`
              flex: 1;
              min-width: 0;
              padding: 8px 12px;
              font-family: 'Inter', sans-serif;
              font-weight: 600;
              font-size: 12px;
              line-height: 14px;
              letter-spacing: 0.5px;
              color: ${day.isToday ? colors.PRIMARY_300 : colors.NEUTRAL_70};
              text-align: center;
              text-transform: uppercase;
              border-right: 1px solid ${colors.SECONDARY_200};

              &:last-child {
                border-right: none;
              }
            `}
          >
            {DAY_NAMES[day.date.getDay()]} {MONTH_ABBREVS[day.date.getMonth()]}{' '}
            {day.dayNumber}
          </div>
        ))}
      </div>

      {/* Week Grid with DnD */}
      {dndEnabled ? (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {gridContent}
          <DragOverlay>
            {activeGame && (
              <div
                css={css`
                  opacity: 0.8;
                  pointer-events: none;
                `}
              >
                <CalendarGameCard game={activeGame} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      ) : (
        gridContent
      )}
    </div>
  )
}

export default WeekGrid
